import React, { useMemo } from 'react';

import useDataSource from 'hooks/useDataSource';
import EmbedMatomo from 'containers/Results/WebsiteResults/EmbedMatomo';
import { EDIT_LICENSE, EDIT_MATOMO } from 'shared/constants/permissions';
import { useRouteMatch, Link } from 'react-router-dom';
import RoutedLayout from 'components/Layout/RoutedLayout';
import Loading from 'components/Loading';
import { generateRoutes } from 'components/Layout/utils';
import EmptyMessage from 'components/EmptyMessage';
import { Button, Result } from 'antd';
import useUser from 'hooks/useUser';
import { hasModuleLicense } from 'shared/utils/license';
import { Module } from 'shared/services/prisma/types';
import routes from './routes';

const extraFields = `
  website {
    id
    matomoId
    matomoVisitDimensions {
      id
      index
      name
      active
    }
    matomoActionDimensions {
      id
      index
      name
      active
    }
    matomoGoals {
      id
      matomoGoalId
      name
    }
    matomoForms {
      id
      name
      autoCreated
    }
    matomoCustomReports {
      id
      name      
    }
    matomoDashboards {
      id
      name
      matomoDashboardId
    }
  }
  isDemo
`;

const mapDimension = category => d => ({
  path: `custom-dimension-${d.index}`,
  label: d.name,
  matomoUrlParams: {
    category,
    subcategory: `customdimension${d.index}`,
  },
});

const getRoutes = (dataSource) => routes.map(route => {
  if (route.path === 'dashboard') {
    const dashboards = dataSource?.website?.matomoDashboards?.map(d => ({
      path: d.matomoDashboardId.toString(),
      label: d.name,
      matomoUrlParams: {
        category: 'Dashboard_Dashboard',
        subcategory: d.matomoDashboardId,
      },
    })) || [];

    return { ...route, dynamicItems: dashboards };
  }

  if (route.path === 'visitors') {
    const customDimensions = dataSource?.website?.matomoVisitDimensions.filter(d => d.active).map(mapDimension('General_Visitors')) || [];

    return { ...route, dynamicItems: customDimensions };
  }

  if (route.path === 'behavior') {
    const customDimensions = dataSource?.website?.matomoActionDimensions.filter(d => d.active).map(mapDimension('General_Actions')) || [];

    return { ...route, dynamicItems: customDimensions };
  }

  if (route.path === 'goals') {
    const goals = dataSource?.website?.matomoGoals?.map(g => ({
      path: g.matomoGoalId.toString(),
      label: g.name,
      matomoUrlParams: {
        category: 'Goals_Goals',
        subcategory: g.matomoGoalId,
      },
    })) || [];

    return { ...route, dynamicItems: goals };
  }

  if (route.path === 'forms') {
    const forms = dataSource?.website?.matomoForms?.filter(f => !f.autoCreated).map(f => ({
      path: f.id.toString(),
      label: f.name,
      matomoUrlParams: {
        category: 'FormAnalytics_Forms',
        subcategory: f.id,
      },
    })) || [];

    return { ...route, dynamicItems: forms };
  }

  if (route.path === 'custom-reports') {
    const customReports = dataSource?.website?.matomoCustomReports?.map(r => ({
      path: r.id.toString(),
      label: r.name,
      matomoUrlParams: {
        category: 'CustomReports_CustomReports',
        subcategory: r.id,
      },
    })) || [];

    return { ...route, dynamicItems: customReports };
  }

  return route;
});

function getCurrentRoute(analyticsRoutes, page, subpage) {
  const mainRoute = analyticsRoutes.find(r => r.path === page);
  const currentRoute = subpage ? mainRoute?.children?.find(r => r.path === subpage || r.path === '*') : mainRoute;
  return { mainRoute, currentRoute };
}

const Analytics = () => {
  const { isSuperUser } = useUser();
  const { params } = useRouteMatch('/data-sources/:dataSourceId/analytics/:page?/:subpage?');
  const { dataSource, dataSourceId, loading, getDataSourceLicenseSetting, hasDataSourcePermission } = useDataSource({ extraFields });

  const analyticsRoutes = useMemo(() => generateRoutes(getRoutes(dataSource), [dataSource]));

  const { mainRoute, currentRoute } = useMemo(() => getCurrentRoute(analyticsRoutes, params.page, params.subpage), [params.page, params.subpage, analyticsRoutes]);

  if (loading) {
    return <Loading />;
  }

  const hasAnalytics = hasModuleLicense(dataSource?.activeLicense, Module.Analytics);

  if (!isSuperUser && !hasAnalytics) {
    return (
      <Result
        status="info"
        title="No license"
        subTitle="You need to have a license for Analytics to view this page."
        extra={hasDataSourcePermission(EDIT_LICENSE) ? (
          <Link to={`/data-sources/${dataSourceId}/settings/license`}>
            <Button type="primary">Upgrade license</Button>
          </Link>
        ) : (
          <>Ask the owner to setup a license.</>
        )}
      />
    );
  }

  let content = null;

  const needLicense = currentRoute?.licenseSetting && !getDataSourceLicenseSetting(currentRoute.licenseSetting);

  if (needLicense) {
    content = (
      <EmptyMessage
        status="info"
        title="License restriction"
        subTitle={`Upgrade your license to access ${currentRoute?.label}`}
      />
    );
  } else if (mainRoute?.noDemoData && dataSource?.isDemo) {
    content = (
      <EmptyMessage
        status="info"
        title="No demo data"
        subTitle={`Unfortunately we dont have any data for ${mainRoute.textLabel || mainRoute.label} in this demo account.`}
        extra={(
          <>
            {mainRoute.readMoreUrl && (
            <a href={mainRoute.readMoreUrl} target="_blank">
              <Button>
                Read more
              </Button>
            </a>
            )}
            <a href="https://academy.extellio.com/contact-support" target="_blank">
              <Button type="primary">Request live demo</Button>
            </a>
          </>
        )}
      />
    );
  }

  return (
    <RoutedLayout className="module-analytics" routes={analyticsRoutes} sidebar>
      {content || (
        <>
          {currentRoute?.component && <currentRoute.component />}
          {currentRoute?.matomoUrlParams && <EmbedMatomo permission={EDIT_MATOMO} urlParams={currentRoute?.matomoUrlParams} />}
        </>
      )}
    </RoutedLayout>
  );
};

export default Analytics;
